import React, { useEffect, useState } from "react";
import { BiSolidVideoPlus } from "react-icons/bi";
import { LuFileCheck2, LuActivity } from "react-icons/lu";
import { FaRegCalendarAlt } from "react-icons/fa";
import { Modal, message, Button, Select, Flex, Spin } from "antd";
import dayjs from "dayjs";
import ConsultationService from "../../../Services/ConsultationService";
import { EyeOutlined } from "@ant-design/icons";
import nodatafound from "../../../Assets/Images/Svg/nodatafound.svg";

function DigitalECG(props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("live");
  const [ecgList, setEcgList] = useState([]);
  const [ecgUrl, setEcgUrl] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getEcg();
  }, [props.patientDetails]);

  const getEcg = async () => {
    if (props.patientDetails && props.patientDetails.patientId) {
      setIsLoading(true);
      const payload = {
        patientId: props.patientDetails.patientId,
        status: 1,
      };
      try {
        const res = await ConsultationService.fetchECGgraph(payload);
        if (Array.isArray(res.data) && res.data.length > 0) {
          setEcgList(res.data);
          setSelectedDate(res.data[0].uploadedDateTime);
        } else {
          setEcgList([]);
          setEcgUrl("");
          setSelectedDate(null);
        }
      } catch (e) {
        setIsLoading(false);
        setEcgList([]);
        setEcgUrl("");
        message.error("Failed to fetch ECG graphs.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const showModal = () => setIsModalOpen(true);
  const handleCancel = () => setIsModalOpen(false);

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);

    if (ecgList.length > 0) {
      setEcgUrl(ecgList[0].resultDocumentName);
      setSelectedDate(ecgList[0].uploadedDateTime);
    }
  };

  const handleDateSelect = (value) => {
    setSelectedDate(value);
    const selectedEcg = ecgList.find((item) => item.uploadedDateTime === value);
    if (selectedEcg) {
      setEcgUrl(selectedEcg.resultDocumentName);
    }
  };

  const handleOpenGraphModal = (url) => {
    setEcgUrl(url);
    setIsModalOpen(true);
  };

  return (
    <div className="bg-transparent w-full h-full flex flex-col overflow-hidden p-3">
      {/* Action Row */}
      <div className="flex items-center gap-4 shrink-0 mb-6 flex-wrap">
        <span
          onClick={() => handleTabSwitch("saved")}
          className="flex items-center gap-2 rounded-lg px-4 py-2 bg-[#ffe8eb] text-[#143250] font-semibold text-sm cursor-pointer"
        >
          <LuFileCheck2 className="text-[#ff4d4f] text-lg" />
          <span>Saved Graphs</span>
        </span>

        {ecgList.length > 0 && (
          <Select
            className="ecg-date-select h-9"
            style={{ width: 180 }}
            value={selectedDate}
            onChange={handleDateSelect}
            placeholder={
              <div className="flex items-center gap-2 text-gray-400">
                <FaRegCalendarAlt />
                <span>Select Date</span>
              </div>
            }
          >
            {ecgList.map((item) => (
              <Select.Option key={item.id} value={item.uploadedDateTime}>
                {dayjs(item.uploadedDateTime).format("DD/MM/YYYY HH:mm")}
              </Select.Option>
            ))}
          </Select>
        )}
      </div>

      {/* ECG Display */}
      <div className="mt-2 flex flex-col flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar flex flex-col gap-3">
          {isLoading && ecgList?.length === 0 ? (
            <div className="flex items-center justify-center mt-10">
              <Flex align="center" gap="middle">
                <Spin size="large" tip="Loading" />
              </Flex>
            </div>
          ) : ecgList?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 bg-white rounded-lg border border-gray-100">
              <img src={nodatafound} alt="nodatafound" className="h-40 w-40 mb-4 opacity-75" />
              <p className="text-gray-500 font-medium">No saved graphs found.</p>
            </div>
          ) : (
            ecgList.map((item, index) => {
              const file = item?.resultDocumentName;
              if (!file) return null;
              
              let finalUrl;
              if (file?.startsWith("http") || file?.startsWith("https")) {
                finalUrl = file;
              } else {
                finalUrl = process.env.REACT_APP_IMAGE_URL + file;
              }

              return (
                <div
                  key={index}
                  className="group bg-white rounded-xl shadow-sm border border-gray-100 p-3.5 flex items-center cursor-pointer hover:shadow-md hover:border-l-4 hover:border-l-[#0aa097] hover:bg-[#F4FDFD] transition-all duration-200"
                >
                  <div className="w-[42px] h-[42px] rounded-full bg-[#E8F8F5] text-[#0aa097] flex items-center justify-center text-xl mr-4 flex-shrink-0">
                    <LuActivity />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#143250] text-[14px] truncate mb-0.5">
                      {item?.labTestName || "ECG 12-Lead"}
                    </p>
                    <p className="text-xs text-gray-500 font-medium">
                      {item?.uploadedDateTime}
                    </p>
                  </div>

                  <a href={finalUrl} target="_blank" rel="noopener noreferrer" className="ml-2">
                    <div className="w-[34px] h-[34px] bg-[#E8F8F5] text-[#0aa097] group-hover:bg-[#0aa097] group-hover:text-white rounded-lg flex items-center justify-center transition-colors">
                      <EyeOutlined className="text-[16px]" title="View Document" />
                    </div>
                  </a>
                </div>
              );
            })
          )}
        </div>
      </div>

      <Modal
        title="ECG"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width={1200}
        height={1200}
      >
        <div style={{ width: "100%" }}>
          <iframe
            src={ecgUrl}
            title="ECG Modal Graph"
            style={{
              width: "100%",
              height: "800px",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              display: "block",
            }}
            frameBorder="0"
            allowFullScreen
          />
        </div>
      </Modal>
    </div>
  );
}

export default DigitalECG;
